import { parse, stringify } from 'yaml';

export type Frontmatter = Record<string, unknown>;

export interface SplitResult {
  frontmatter: Frontmatter;
  body: string;
  /** Gesetzt, wenn ein Frontmatter-Block existierte, aber kein gültiges YAML-Objekt war. */
  error?: string;
}

const FM_RE = /^﻿?---[ \t]*\r?\n([\s\S]*?)\r?\n?---[ \t]*(?:\r?\n|$)/;

/**
 * Trennt YAML-Frontmatter vom Markdown-Inhalt.
 * Ungültiges YAML bleibt unverändert im Body, damit nichts verloren geht.
 */
export function splitFrontmatter(md: string): SplitResult {
  const src = md.replace(/^﻿/, '');
  const m = FM_RE.exec(src);
  if (!m) return { frontmatter: {}, body: src };
  const yamlText = m[1] ?? '';
  try {
    const parsed: unknown = yamlText.trim() === '' ? {} : parse(yamlText);
    if (parsed === null || parsed === undefined) return { frontmatter: {}, body: src.slice(m[0].length) };
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { frontmatter: {}, body: src, error: 'Frontmatter ist kein YAML-Objekt' };
    }
    return { frontmatter: toPlain(parsed) as Frontmatter, body: src.slice(m[0].length) };
  } catch (e) {
    return { frontmatter: {}, body: src, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Setzt Frontmatter und Body wieder zu einer Markdown-Datei zusammen. */
export function joinFrontmatter(frontmatter: Frontmatter, body: string): string {
  if (!frontmatter || Object.keys(frontmatter).length === 0) return body;
  const y = stringify(frontmatter, { lineWidth: 0 }).trimEnd();
  return `---\n${y}\n---\n${body}`;
}

/** Wandelt YAML-Sonderwerte (Date etc.) in JSON-taugliche Werte um. */
function toPlain(v: unknown): unknown {
  if (v instanceof Date) {
    const iso = v.toISOString();
    return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso;
  }
  if (Array.isArray(v)) return v.map(toPlain);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = toPlain(val);
    return out;
  }
  return v;
}

/**
 * Vergleicht zwei JSON-Werte unabhängig von der Schlüsselreihenfolge
 * (PostgreSQL-JSONB sortiert Objektschlüssel um).
 */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a)) return Array.isArray(b) && a.length === b.length && a.every((x, i) => sameJson(x, b[i]));
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(b)) {
    const ka = Object.keys(a);
    if (ka.length !== Object.keys(b).length) return false;
    return ka.every((k) => Object.hasOwn(b, k) && sameJson((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/** JSON mit sortierten Objektschlüsseln (stabil für Hashes). */
export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v) ?? 'null';
}
